import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import OrganizerLayout from '../components/layouts/OrganizerLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  FileSpreadsheet, FileText, Download, Users, CreditCard, 
  ClipboardList, UserCheck, Building2, Loader2, RefreshCw,
  CheckCircle2, XCircle, Clock, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('attendees');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [events, setEvents] = useState([]);
  
  // Report data
  const [attendees, setAttendees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [checkinStats, setCheckinStats] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      fetchAllData();
    }
  }, [selectedEventId]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEventId(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load events');
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [attendeesRes, paymentsRes, committeesRes, tasksRes, checkinRes] = await Promise.all([
        axios.get(`${API_URL}/api/event/attendees`, { headers }),
        axios.get(`${API_URL}/api/event/payments?event_id=${selectedEventId}`, { headers }),
        axios.get(`${API_URL}/api/event/committees?event_id=${selectedEventId}`, { headers }),
        axios.get(`${API_URL}/api/event/tasks?event_id=${selectedEventId}`, { headers }),
        axios.get(`${API_URL}/api/event/checkin/status?event_id=${selectedEventId}`, { headers })
      ]);
      
      setAttendees(attendeesRes.data);
      setPayments(paymentsRes.data);
      setCommittees(committeesRes.data);
      setTasks(tasksRes.data);
      setCheckinStats(checkinRes.data);
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };

  // Export to Excel
  const exportToExcel = (data, filename, sheetName) => {
    setExporting(true);
    try {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      
      // Auto-width columns
      const colWidths = Object.keys(data[0] || {}).map(key => ({
        wch: Math.max(key.length, ...data.map(row => String(row[key] || '').length)) + 2
      }));
      ws['!cols'] = colWidths;
      
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `${filename}.xlsx`);
      toast.success('Excel file downloaded!');
    } catch (error) {
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  // Export to PDF
  const exportToPDF = (data, columns, title, filename) => {
    setExporting(true);
    try {
      const doc = new jsPDF('landscape');
      const eventName = events.find(e => e.id === selectedEventId)?.name || 'Event';
      
      // Header
      doc.setFontSize(20);
      doc.setTextColor(128, 0, 32); // Maroon color
      doc.text(title, 14, 22);
      
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(`Event: ${eventName}`, 14, 32);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-PH')}`, 14, 40);
      
      // Table
      doc.autoTable({
        startY: 50,
        head: [columns.map(col => col.header)],
        body: data.map(row => columns.map(col => row[col.key] || '-')),
        theme: 'striped',
        headStyles: {
          fillColor: [128, 0, 32], // Maroon
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        styles: {
          fontSize: 9,
          cellPadding: 4
        }
      });
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Page ${i} of ${pageCount} | batchm8s Reunion Management System`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }
      
      doc.save(`${filename}.pdf`);
      toast.success('PDF file downloaded!');
    } catch (error) {
      console.error(error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // Prepare data for export
  const getAttendeeExportData = () => {
    return attendees.map((a, idx) => ({
      '#': idx + 1,
      'First Name': a.first_name || '',
      'Last Name': a.last_name || '',
      'Email': a.email || '',
      'Batch Year': a.batch_year || '',
      'Phone': a.phone || '',
      'Location': a.current_location || '',
      'Committees': (a.committees || []).map(c => c.name).join(', ') || 'None',
      'Checked In': a.checked_in ? 'Yes' : 'No',
      'Registered': formatDate(a.created_at)
    }));
  };

  const getPaymentExportData = () => {
    return payments.map((p, idx) => ({
      '#': idx + 1,
      'Name': p.user_name || '',
      'Amount': formatCurrency(p.amount),
      'Type': p.payment_type || '',
      'Method': p.payment_method || '',
      'Reference': p.reference_number || '',
      'Status': p.status || '',
      'Date': formatDate(p.created_at),
      'Verified By': p.verified_by_name || '-'
    }));
  };

  const getCommitteeExportData = () => {
    const data = [];
    committees.forEach(c => {
      // Add head
      if (c.head_name) {
        data.push({
          'Committee': c.name,
          'Member Name': c.head_name,
          'Role': 'Head',
          'Email': c.head_email || ''
        });
      }
      // Add members
      (c.members || []).forEach(m => {
        data.push({
          'Committee': c.name,
          'Member Name': m.name,
          'Role': 'Member',
          'Email': m.email || ''
        });
      });
    });
    return data;
  };

  const getTaskExportData = () => {
    return tasks.map((t, idx) => ({
      '#': idx + 1,
      'Title': t.title || '',
      'Category': t.category || '',
      'Status': t.status || '',
      'Assigned To': t.assigned_to_name || 'Unassigned',
      'Due Date': formatDate(t.due_date),
      'Reports': (t.reports || []).length,
      'Created': formatDate(t.created_at)
    }));
  };

  const getCheckinExportData = () => {
    return attendees.map((a, idx) => ({
      '#': idx + 1,
      'Name': `${a.first_name} ${a.last_name}`,
      'Email': a.email || '',
      'Batch Year': a.batch_year || '',
      'Status': a.checked_in ? 'Checked In' : 'Not Checked In',
      'Check-in Time': a.checked_in_at ? formatDate(a.checked_in_at) : '-'
    }));
  };

  // Summary stats
  const totalPayments = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const verifiedPayments = payments.filter(p => p.status === 'verified');
  const pendingPayments = payments.filter(p => p.status === 'pending');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  if (loading && !selectedEventId) {
    return (
      <OrganizerLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-maroon" />
        </div>
      </OrganizerLayout>
    );
  }

  return (
    <OrganizerLayout>
      <div className="space-y-6 animate-fade-in" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
              <FileSpreadsheet className="h-8 w-8" />
              Reports & Export
            </h1>
            <p className="text-gray-600 mt-1">Generate and download event reports</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger className="w-[250px]" data-testid="event-select">
                <SelectValue placeholder="Select event" />
              </SelectTrigger>
              <SelectContent>
                {events.map(event => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <Users className="h-8 w-8 mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold text-gray-900">{attendees.length}</p>
              <p className="text-xs text-gray-500">Total Attendees</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <CreditCard className="h-8 w-8 mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalPayments)}</p>
              <p className="text-xs text-gray-500">Total Collected</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <Building2 className="h-8 w-8 mx-auto text-purple-500 mb-2" />
              <p className="text-2xl font-bold text-gray-900">{committees.length}</p>
              <p className="text-xs text-gray-500">Committees</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <ClipboardList className="h-8 w-8 mx-auto text-amber-500 mb-2" />
              <p className="text-2xl font-bold text-gray-900">{completedTasks.length}/{tasks.length}</p>
              <p className="text-xs text-gray-500">Tasks Done</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-maroon text-white">
            <CardContent className="p-4 text-center">
              <UserCheck className="h-8 w-8 mx-auto mb-2" />
              <p className="text-2xl font-bold">{checkinStats?.percentage || 0}%</p>
              <p className="text-xs text-white/80">Check-in Rate</p>
            </CardContent>
          </Card>
        </div>

        {/* Report Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-cream-paper p-1 rounded-xl">
            <TabsTrigger value="attendees" className="rounded-lg data-[state=active]:bg-maroon data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-2" />
              Attendees
            </TabsTrigger>
            <TabsTrigger value="payments" className="rounded-lg data-[state=active]:bg-maroon data-[state=active]:text-white">
              <CreditCard className="h-4 w-4 mr-2" />
              Payments
            </TabsTrigger>
            <TabsTrigger value="committees" className="rounded-lg data-[state=active]:bg-maroon data-[state=active]:text-white">
              <Building2 className="h-4 w-4 mr-2" />
              Committees
            </TabsTrigger>
            <TabsTrigger value="tasks" className="rounded-lg data-[state=active]:bg-maroon data-[state=active]:text-white">
              <ClipboardList className="h-4 w-4 mr-2" />
              Tasks
            </TabsTrigger>
            <TabsTrigger value="checkin" className="rounded-lg data-[state=active]:bg-maroon data-[state=active]:text-white">
              <UserCheck className="h-4 w-4 mr-2" />
              Check-in
            </TabsTrigger>
          </TabsList>

          {/* Attendees Report */}
          <TabsContent value="attendees">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl text-maroon">Attendee Report</CardTitle>
                  <CardDescription>{attendees.length} registered attendees</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportToExcel(getAttendeeExportData(), 'attendees-report', 'Attendees')}
                    disabled={exporting || attendees.length === 0}
                    className="gap-2"
                    data-testid="export-attendees-excel"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Excel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => exportToPDF(
                      getAttendeeExportData(),
                      [
                        { header: '#', key: '#' },
                        { header: 'First Name', key: 'First Name' },
                        { header: 'Last Name', key: 'Last Name' },
                        { header: 'Email', key: 'Email' },
                        { header: 'Batch', key: 'Batch Year' },
                        { header: 'Committees', key: 'Committees' },
                        { header: 'Checked In', key: 'Checked In' }
                      ],
                      'Attendee Report',
                      'attendees-report'
                    )}
                    disabled={exporting || attendees.length === 0}
                    className="gap-2 bg-maroon hover:bg-maroon-hover"
                    data-testid="export-attendees-pdf"
                  >
                    <FileText className="h-4 w-4" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-maroon" />
                  </div>
                ) : attendees.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No attendees registered yet</p>
                  </div>
                ) : (
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-cream-paper">
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Batch</TableHead>
                          <TableHead>Committees</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {attendees.slice(0, 10).map((a, idx) => (
                          <TableRow key={a.id}>
                            <TableCell className="font-medium">{idx + 1}</TableCell>
                            <TableCell className="font-medium">{a.first_name} {a.last_name}</TableCell>
                            <TableCell className="text-gray-500">{a.email}</TableCell>
                            <TableCell>{a.batch_year || '-'}</TableCell>
                            <TableCell>
                              {(a.committees || []).length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {a.committees.slice(0, 2).map((c, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">
                                      {c.name.replace(' Committee', '')}
                                    </Badge>
                                  ))}
                                  {a.committees.length > 2 && (
                                    <Badge variant="outline" className="text-xs">+{a.committees.length - 2}</Badge>
                                  )}
                                </div>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              {a.checked_in ? (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  In
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-500">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {attendees.length > 10 && (
                      <div className="p-3 bg-cream-paper text-center text-sm text-gray-500">
                        Showing 10 of {attendees.length} attendees. Export to see all.
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Report */}
          <TabsContent value="payments">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl text-maroon">Payment Report</CardTitle>
                  <CardDescription>
                    {payments.length} payments | {verifiedPayments.length} verified | {pendingPayments.length} pending
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportToExcel(getPaymentExportData(), 'payments-report', 'Payments')}
                    disabled={exporting || payments.length === 0}
                    className="gap-2"
                    data-testid="export-payments-excel"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Excel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => exportToPDF(
                      getPaymentExportData(),
                      [
                        { header: '#', key: '#' },
                        { header: 'Name', key: 'Name' },
                        { header: 'Amount', key: 'Amount' },
                        { header: 'Type', key: 'Type' },
                        { header: 'Method', key: 'Method' },
                        { header: 'Reference', key: 'Reference' },
                        { header: 'Status', key: 'Status' },
                        { header: 'Date', key: 'Date' }
                      ],
                      'Payment Report',
                      'payments-report'
                    )}
                    disabled={exporting || payments.length === 0}
                    className="gap-2 bg-maroon hover:bg-maroon-hover"
                    data-testid="export-payments-pdf"
                  >
                    <FileText className="h-4 w-4" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-maroon" />
                  </div>
                ) : payments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CreditCard className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No payments recorded yet</p>
                  </div>
                ) : (
                  <>
                    {/* Payment Summary */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-green-50 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-green-700">{formatCurrency(verifiedPayments.reduce((s, p) => s + p.amount, 0))}</p>
                        <p className="text-sm text-green-600">Verified</p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-amber-700">{formatCurrency(pendingPayments.reduce((s, p) => s + p.amount, 0))}</p>
                        <p className="text-sm text-amber-600">Pending</p>
                      </div>
                      <div className="bg-maroon/10 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-maroon">{formatCurrency(totalPayments)}</p>
                        <p className="text-sm text-maroon/70">Total</p>
                      </div>
                    </div>
                    
                    <div className="rounded-lg border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-cream-paper">
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Method</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {payments.slice(0, 10).map((p, idx) => (
                            <TableRow key={p.id}>
                              <TableCell className="font-medium">{idx + 1}</TableCell>
                              <TableCell className="font-medium">{p.user_name}</TableCell>
                              <TableCell className="font-bold text-maroon">{formatCurrency(p.amount)}</TableCell>
                              <TableCell className="capitalize">{p.payment_type}</TableCell>
                              <TableCell className="uppercase text-xs">{p.payment_method}</TableCell>
                              <TableCell>
                                {p.status === 'verified' ? (
                                  <Badge className="bg-green-100 text-green-700">Verified</Badge>
                                ) : p.status === 'rejected' ? (
                                  <Badge className="bg-red-100 text-red-700">Rejected</Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-gray-500">{formatDate(p.created_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {payments.length > 10 && (
                        <div className="p-3 bg-cream-paper text-center text-sm text-gray-500">
                          Showing 10 of {payments.length} payments. Export to see all.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Committees Report */}
          <TabsContent value="committees">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl text-maroon">Committee Report</CardTitle>
                  <CardDescription>{committees.length} committees formed</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportToExcel(getCommitteeExportData(), 'committees-report', 'Committees')}
                    disabled={exporting || committees.length === 0}
                    className="gap-2"
                    data-testid="export-committees-excel"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Excel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => exportToPDF(
                      getCommitteeExportData(),
                      [
                        { header: 'Committee', key: 'Committee' },
                        { header: 'Member Name', key: 'Member Name' },
                        { header: 'Role', key: 'Role' },
                        { header: 'Email', key: 'Email' }
                      ],
                      'Committee Report',
                      'committees-report'
                    )}
                    disabled={exporting || committees.length === 0}
                    className="gap-2 bg-maroon hover:bg-maroon-hover"
                    data-testid="export-committees-pdf"
                  >
                    <FileText className="h-4 w-4" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-maroon" />
                  </div>
                ) : committees.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No committees formed yet</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {committees.map(c => (
                      <div key={c.id} className="rounded-xl border p-4 bg-gradient-to-r from-white to-cream-paper">
                        <div className="flex items-center gap-3 mb-3">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: c.color || '#800020' }}
                          />
                          <h3 className="font-semibold text-gray-900">{c.name}</h3>
                        </div>
                        {c.head_name && (
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-gold text-gray-900 text-xs">Head</Badge>
                            <span className="text-sm">{c.head_name}</span>
                          </div>
                        )}
                        {(c.members || []).length > 0 && (
                          <div className="text-sm text-gray-500">
                            + {c.members.length} member{c.members.length > 1 ? 's' : ''}
                          </div>
                        )}
                        {!c.head_name && (c.members || []).length === 0 && (
                          <p className="text-sm text-gray-400 italic">No members assigned</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tasks Report */}
          <TabsContent value="tasks">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl text-maroon">Task Report</CardTitle>
                  <CardDescription>
                    {tasks.length} tasks | {completedTasks.length} completed | {tasks.length - completedTasks.length} pending
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportToExcel(getTaskExportData(), 'tasks-report', 'Tasks')}
                    disabled={exporting || tasks.length === 0}
                    className="gap-2"
                    data-testid="export-tasks-excel"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Excel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => exportToPDF(
                      getTaskExportData(),
                      [
                        { header: '#', key: '#' },
                        { header: 'Title', key: 'Title' },
                        { header: 'Category', key: 'Category' },
                        { header: 'Status', key: 'Status' },
                        { header: 'Assigned To', key: 'Assigned To' },
                        { header: 'Due Date', key: 'Due Date' },
                        { header: 'Reports', key: 'Reports' }
                      ],
                      'Task Report',
                      'tasks-report'
                    )}
                    disabled={exporting || tasks.length === 0}
                    className="gap-2 bg-maroon hover:bg-maroon-hover"
                    data-testid="export-tasks-pdf"
                  >
                    <FileText className="h-4 w-4" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-maroon" />
                  </div>
                ) : tasks.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <ClipboardList className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No tasks created yet</p>
                  </div>
                ) : (
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-cream-paper">
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Task</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Assigned To</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Reports</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tasks.map((t, idx) => (
                          <TableRow key={t.id}>
                            <TableCell className="font-medium">{idx + 1}</TableCell>
                            <TableCell className="font-medium">{t.title}</TableCell>
                            <TableCell className="capitalize">{t.category}</TableCell>
                            <TableCell>{t.assigned_to_name || '-'}</TableCell>
                            <TableCell className="text-gray-500">{formatDate(t.due_date)}</TableCell>
                            <TableCell>
                              {(t.reports || []).length > 0 ? (
                                <Badge variant="outline" className="text-blue-600">
                                  {t.reports.length}
                                </Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {t.status === 'completed' ? (
                                <Badge className="bg-green-100 text-green-700">Done</Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Check-in Report */}
          <TabsContent value="checkin">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl text-maroon">Check-in Report</CardTitle>
                  <CardDescription>
                    {checkinStats?.checked_in || 0} of {checkinStats?.total_attendees || 0} checked in ({checkinStats?.percentage || 0}%)
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportToExcel(getCheckinExportData(), 'checkin-report', 'Check-in')}
                    disabled={exporting || attendees.length === 0}
                    className="gap-2"
                    data-testid="export-checkin-excel"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Excel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => exportToPDF(
                      getCheckinExportData(),
                      [
                        { header: '#', key: '#' },
                        { header: 'Name', key: 'Name' },
                        { header: 'Email', key: 'Email' },
                        { header: 'Batch', key: 'Batch Year' },
                        { header: 'Status', key: 'Status' },
                        { header: 'Check-in Time', key: 'Check-in Time' }
                      ],
                      'Check-in Report',
                      'checkin-report'
                    )}
                    disabled={exporting || attendees.length === 0}
                    className="gap-2 bg-maroon hover:bg-maroon-hover"
                    data-testid="export-checkin-pdf"
                  >
                    <FileText className="h-4 w-4" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-maroon" />
                  </div>
                ) : (
                  <>
                    {/* Check-in Progress */}
                    <div className="mb-6">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Check-in Progress</span>
                        <span className="font-bold text-maroon">{checkinStats?.percentage || 0}%</span>
                      </div>
                      <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-maroon to-gold rounded-full transition-all duration-500"
                          style={{ width: `${checkinStats?.percentage || 0}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs mt-2 text-gray-500">
                        <span>{checkinStats?.checked_in || 0} checked in</span>
                        <span>{checkinStats?.not_checked_in || 0} not yet</span>
                      </div>
                    </div>

                    <div className="rounded-lg border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-cream-paper">
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Batch</TableHead>
                            <TableHead className="text-center">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {attendees.slice(0, 10).map((a, idx) => (
                            <TableRow key={a.id}>
                              <TableCell className="font-medium">{idx + 1}</TableCell>
                              <TableCell className="font-medium">{a.first_name} {a.last_name}</TableCell>
                              <TableCell className="text-gray-500">{a.email}</TableCell>
                              <TableCell>{a.batch_year || '-'}</TableCell>
                              <TableCell className="text-center">
                                {a.checked_in ? (
                                  <Badge className="bg-green-100 text-green-700">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Checked In
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-gray-500">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Not Yet
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {attendees.length > 10 && (
                        <div className="p-3 bg-cream-paper text-center text-sm text-gray-500">
                          Showing 10 of {attendees.length} attendees. Export to see all.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </OrganizerLayout>
  );
};

export default Reports;
